import React from 'react';
import { cn } from 'lib/utils';
import type { ResultAccent } from './accents';

type AccentStyle = React.CSSProperties & { '--ra-fg': string; '--ra-bg': string };

const accentStyle = (accent: ResultAccent): AccentStyle => ({ '--ra-fg': accent.fg, '--ra-bg': accent.bg });

/** Small mono caps label used above every figure on a result. */
export function Eyebrow({ children, accent, className }: { children: React.ReactNode; accent?: boolean; className?: string }) {
  return (
    <span
      className={cn(
        'font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] sm:text-[11px]',
        accent ? 'text-[color:var(--ra-fg)]' : 'text-[#646D84] dark:text-muted-foreground',
        className,
      )}
    >
      {children}
    </span>
  );
}

interface ResultHeroProps {
  accent: ResultAccent;
  /** Decorative line-art drawn big and faint in the corner. */
  watermark?: React.ReactNode;
  /** The headline figure and what it means. */
  children: React.ReactNode;
  /** Section tiles to the right on desktop, under the figure on phones. */
  aside?: React.ReactNode;
  label: string;
  /** Stack the figure over its tiles at every width: for a narrow column such as the public result card. */
  compact?: boolean;
  className?: string;
}

/** The tinted hero every result opens with: the headline on the left, its parts on the right. */
export function ResultHero({ accent, watermark, children, aside, label, compact, className }: ResultHeroProps) {
  return (
    <section
      aria-label={label}
      style={accentStyle(accent)}
      className={cn(
        'relative grid items-center gap-6 overflow-hidden rounded-3xl bg-[var(--ra-bg)] p-5 text-[#10182B] sm:p-7 lg:gap-9 lg:rounded-[28px] lg:p-9',
        aside && !compact && 'lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]',
        className,
      )}
    >
      {watermark && (
        <span aria-hidden className="pointer-events-none absolute -right-8 -top-12 h-[200px] w-[200px] text-[color:var(--ra-fg)] opacity-10 lg:h-[340px] lg:w-[340px]">
          {watermark}
        </span>
      )}
      <div className="relative min-w-0">{children}</div>
      {aside && <div className="relative min-w-0">{aside}</div>}
    </section>
  );
}

/** The giant figure. 96px on phones, 132px on desktop. */
export function BigFigure({ children, className, label }: { children: React.ReactNode; className?: string; label?: string }) {
  return (
    <div
      aria-label={label}
      className={cn('mt-2 font-display text-[88px] font-extrabold leading-[0.9] tracking-[-0.06em] tabular-nums sm:text-[96px] lg:text-[132px]', className)}
    >
      {children}
    </div>
  );
}

/** White rounded pill: CEFR, target, attempt. `dark` for the one that matters (the goal). */
export function Chip({ children, dark, className }: { children: React.ReactNode; dark?: boolean; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[10px] border px-3 py-1.5 text-xs font-bold lg:text-[13px]',
        dark ? 'border-[#10182B] bg-[#10182B] text-white' : 'border-[#E6E9F2] bg-white text-[#10182B]',
        className,
      )}
    >
      {children}
    </span>
  );
}

interface HeroTileProps {
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  /** 0-100 fill of the bar; omit for no bar. */
  percent?: number;
  className?: string;
}

/** A white tile inside the hero: one section's score. */
export function HeroTile({ label, value, detail, percent, className }: HeroTileProps) {
  return (
    <div className={cn('rounded-2xl bg-white p-3.5 lg:rounded-[18px] lg:px-[18px] lg:py-4', className)}>
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-1.5 flex items-baseline justify-between gap-2">
        <b className="font-display text-[28px] font-extrabold tracking-[-0.04em] tabular-nums lg:text-[34px]">{value}</b>
        {detail && <span className="text-[11.5px] text-[#4B5468] lg:text-[12.5px]">{detail}</span>}
      </div>
      {percent !== undefined && <Meter percent={percent} className="mt-2.5" />}
    </div>
  );
}

/** A thin bar. Colour comes from the hero accent unless `color` is given. */
export function Meter({ percent, color, className }: { percent: number; color?: string; className?: string }) {
  const width = Math.max(0, Math.min(100, percent));
  return (
    <div aria-hidden className={cn('h-1.5 overflow-hidden rounded-full bg-[#EEF1F8] dark:bg-white/10 lg:h-[7px]', className)}>
      <i className="block h-full rounded-full bg-[color:var(--ra-fg,#10182B)]" style={{ width: `${width}%`, ...(color ? { background: color } : null) }} />
    </div>
  );
}

interface ResultCardProps {
  title?: string;
  subtitle?: string;
  /** Right of the title: a count or a short note. */
  aside?: React.ReactNode;
  accent?: ResultAccent;
  children: React.ReactNode;
  className?: string;
}

/** A white section card with the display-font title the design uses. */
export function ResultCard({ title, subtitle, aside, accent, children, className }: ResultCardProps) {
  const id = React.useId();
  return (
    <section
      aria-labelledby={title ? id : undefined}
      style={accent ? accentStyle(accent) : undefined}
      className={cn('rounded-[22px] border border-[#E6E9F2] bg-card p-[18px] dark:border-border sm:p-6', className)}
    >
      {(title || aside) && (
        <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          {title && (
            <h2 id={id} className="font-display text-[17px] font-extrabold tracking-[-0.03em] text-foreground sm:text-xl">
              {title}
            </h2>
          )}
          {aside && <span className="text-[12.5px] text-[#646D84] dark:text-muted-foreground">{aside}</span>}
        </header>
      )}
      {subtitle && <p className="mt-1 text-[12.5px] text-[#646D84] dark:text-muted-foreground">{subtitle}</p>}
      {children}
    </section>
  );
}

interface MeterRowProps {
  label: React.ReactNode;
  /** Right-aligned figure: "7.0", "11 / 14", "78%". */
  value: React.ReactNode;
  percent: number;
  color?: string;
  note?: React.ReactNode;
}

/** Label and figure on one line, a bar below, an optional one-line note. */
export function MeterRow({ label, value, percent, color, note }: MeterRowProps) {
  return (
    <div className="mt-3 first:mt-0">
      <div className="flex justify-between gap-3 text-[13px] font-semibold text-foreground lg:text-sm">
        <span className="min-w-0">{label}</span>
        <span className="shrink-0 tabular-nums">{value}</span>
      </div>
      <Meter percent={percent} color={color} className="mt-1.5" />
      {note && <p className="mt-1.5 text-xs leading-normal text-[#4B5468] dark:text-muted-foreground lg:text-[12.5px]">{note}</p>}
    </div>
  );
}

export type MapState = 'ok' | 'no' | 'skip';

const MAP_STATE_CLASS: Record<MapState, string> = {
  ok: 'border-[#86C9A0] bg-[#E6F4EC] text-[#166534]',
  no: 'border-[#F0A8A2] bg-[#FDECEC] text-[#B42318]',
  skip: 'border-[#CBD2E3] bg-white text-[#4B5468] dark:bg-transparent',
};

const MAP_STATE_MARK: Record<MapState, string> = { ok: '✓', no: '✗', skip: '–' };

export interface MapCell {
  n: number;
  state: MapState;
}

interface AnswerMapProps {
  cells: MapCell[];
  /** Question navigation: a tap on a cell. */
  onSelect?: (n: number) => void;
  /** Labels for assistive tech: `${n}: ${state}` is built from these. */
  stateLabels: Record<MapState, string>;
  /** Shows ✓/✗/– instead of the number (small maps with few questions). */
  marks?: boolean;
  className?: string;
}

/** One cell per question: green right, red wrong, plain skipped. 8 across on phones, 20 on desktop. */
export function AnswerMap({ cells, onSelect, stateLabels, marks, className }: AnswerMapProps) {
  return (
    <div className={cn('mt-3.5 grid grid-cols-8 gap-[5px] sm:grid-cols-10 lg:grid-cols-[repeat(20,minmax(0,1fr))]', className)}>
      {cells.map((cell) => {
        const cls = cn(
          'grid h-[30px] place-items-center rounded-[7px] border-[1.5px] text-[11px] font-bold tabular-nums lg:h-7 lg:text-[11.5px]',
          MAP_STATE_CLASS[cell.state],
        );
        const label = `${cell.n}: ${stateLabels[cell.state]}`;
        const content = marks ? MAP_STATE_MARK[cell.state] : cell.n;
        return onSelect ? (
          <button key={cell.n} type="button" aria-label={label} onClick={() => onSelect(cell.n)} className={cn(cls, 'transition-transform hover:-translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#10182B]')}>
            {content}
          </button>
        ) : (
          <span key={cell.n} role="img" aria-label={label} className={cls}>
            {content}
          </span>
        );
      })}
    </div>
  );
}
